import React, { useEffect, useState } from 'react';
import { Wrench, Loader2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ToolsPage — thin container that mounts the upstream tuning-tools
 * application inside an iframe. Everything else (navigation between
 * Main / Convert / Checksum / Volvo TRW / DTC Lookup, file processing,
 * calibration DBs) lives on the upstream server and is served through
 * the admin-only reverse proxy at `/api/admin/tools/hpt/`.
 *
 * ## Auth handshake
 *
 * The proxy is admin-only. Iframe navigations can't attach an
 * `Authorization: Bearer` header, so we POST to
 * `/api/admin/tools/hpt-session` first (with the standard Bearer
 * token) — the backend sets a short-lived HTTP-only cookie scoped to
 * the proxy path. Only THEN do we mount the iframe. Same origin, so
 * the cookie rides every request the upstream shell makes back to
 * `/api/admin/tools/hpt/*` (file uploads, job polling, downloads).
 *
 * ## Why a single iframe (per 2026-08-04 TZ from tools-server owner)
 *
 *   • the upstream app carries its own left-side navigation across all
 *     five tool families, so wrapping it in additional category tabs
 *     here only duplicates navigation and prevents new tools from
 *     appearing automatically;
 *   • upstream ships `Cache-Control: no-cache, must-revalidate`, so any
 *     server-side update is picked up on the next load — no coordinated
 *     deploy required;
 *   • direct browser access to `hpt.auto-remap.com` will be blocked at
 *     the upstream firewall, so the "open in new tab" affordance is
 *     intentionally omitted.
 */
export default function ToolsPage() {
  const [status, setStatus] = useState('opening'); // opening | ready | error
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const openSession = async () => {
      try {
        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        if (!token) {
          if (!cancelled) { setStatus('error'); setError('Not logged in.'); }
          return;
        }
        const r = await fetch(`${API_URL}/api/admin/tools/hpt-session`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          if (!cancelled) { setStatus('error'); setError(j.detail || `HTTP ${r.status}`); }
          return;
        }
        if (!cancelled) setStatus('ready');
      } catch (e) {
        if (!cancelled) { setStatus('error'); setError(String(e)); }
      }
    };
    openSession();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tools-page">
      {/* Tools uses a 70% viewport-width layout — wide enough for the
          data-dense views inside the iframe (Checksum segment tables,
          DTC/SPN lists) but not so wide that lines become unreadable
          on ultra-wide monitors. Standard admin `max-w-7xl` was too
          narrow (~1075 px on a 2000 px display); pure `w-full` was too
          wide (1896 px). 70vw with a sensible min gives a good middle. */}
      <div className="mx-auto py-4 sm:py-6" style={{ width: '70vw', minWidth: 320, maxWidth: '100%' }}>
        {/* Hero */}
        <div className="mb-3 sm:mb-4">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 text-white inline-flex items-center justify-center">
              <Wrench className="w-4 h-4 sm:w-5 sm:h-5" />
            </span>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight">Tools</h1>
          </div>
        </div>

        {status === 'opening' && (
          <div
            className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500 bg-white border border-slate-200 rounded-lg"
            data-testid="tools-opening"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Opening tools session…</span>
          </div>
        )}

        {status === 'error' && (
          <div
            className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg text-sm"
            data-testid="tools-error"
          >
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold mb-1">Could not open tools session</div>
              <div className="opacity-80">{error}</div>
            </div>
          </div>
        )}

        {status === 'ready' && (
          <iframe
            src={`${API_URL}/api/admin/tools/hpt/`}
            title="AR Tools"
            data-testid="tools-iframe"
            style={{
              width: '100%',
              // Match the vertical rhythm the upstream app is designed
              // for; explicit min-height keeps it usable on shorter
              // laptops where 82vh collapses below the shell nav.
              height: 'calc(100vh - 140px)',
              minHeight: 600,
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              background: '#fff',
              display: 'block',
            }}
            sandbox="allow-same-origin allow-scripts allow-downloads allow-forms allow-popups"
          />
        )}
      </div>
    </div>
  );
}
